import { ComponentType } from 'react';
import { LocalizedText } from '@mews/framework';
import { LocalizedSmallParagraph, useDialogs } from '@mews-commander/ui';
import { Button, Stack, Dialog } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { PasswordField } from '@mews-commander/form';
import { useVerifyPasswordForm } from './useVerifyPasswordForm';
import { VERIFY_PASSWORD_FORM_ID } from './constants';

const VERIFY_PASSWORD_DIALOG_NAME = 'verifyPassword';

const VerifyPasswordDialog = ({
    onSuccess,
    descriptionTextKey,
}: {
    onSuccess: (password: string) => void;
    descriptionTextKey?: Mews.TextKey;
}) => {
    const { closeDialog } = useDialogs();
    const { isLoading, renderFormContent } = useVerifyPasswordForm(onSuccess);

    return (
        <Dialog
            title={<LocalizedText textKey={TextKey.EnterYourPassword} />}
            size="small"
            active
            loading={isLoading}
            onClose={() => closeDialog(VERIFY_PASSWORD_DIALOG_NAME)}
            footer={{
                secondary: (
                    <Button type="button" onClick={() => closeDialog(VERIFY_PASSWORD_DIALOG_NAME)} variant="tertiary">
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                ),
                primary: (
                    <Button type="submit" form={VERIFY_PASSWORD_FORM_ID} variant="primary">
                        <LocalizedText textKey={TextKey.Continue} />
                    </Button>
                ),
            }}
        >
            {renderFormContent(
                <Stack vertical spacing="200">
                    {descriptionTextKey ? (
                        <LocalizedSmallParagraph textKey={descriptionTextKey} variant="secondary" />
                    ) : null}

                    <PasswordField
                        required
                        name="password"
                        label={<LocalizedText textKey={TextKey.Password} />}
                        autoComplete="current-password"
                    />
                </Stack>
            )}
        </Dialog>
    );
};

export const usePasswordGatedDialog = () => {
    const { openDialog, closeDialog } = useDialogs();

    return (GatedDialog: ComponentType<{ password: string }>, dialogId: string, descriptionTextKey?: Mews.TextKey) => {
        const onSuccess = (password: string) => {
            closeDialog(VERIFY_PASSWORD_DIALOG_NAME);

            openDialog(<GatedDialog password={password} />, dialogId);
        };

        openDialog(
            <VerifyPasswordDialog onSuccess={onSuccess} descriptionTextKey={descriptionTextKey} />,
            VERIFY_PASSWORD_DIALOG_NAME
        );
    };
};

export const usePasswordGatedCallback = () => {
    const { openDialog, closeDialog } = useDialogs();

    return ({
        onSuccess,
        descriptionTextKey,
    }: {
        onSuccess: (password: string) => void;
        descriptionTextKey?: Mews.TextKey;
    }) => {
        const handleSuccess = (password: string) => {
            closeDialog(VERIFY_PASSWORD_DIALOG_NAME);
            onSuccess(password);
        };

        openDialog(
            <VerifyPasswordDialog onSuccess={handleSuccess} descriptionTextKey={descriptionTextKey} />,
            VERIFY_PASSWORD_DIALOG_NAME
        );
    };
};
